import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';
import { useTransition, useRef, useCallback, useMemo, useState, startTransition } from 'react';
import { debounce } from 'lodash';
import { Plus, UserRoundSearch } from 'lucide-react';
import { toast } from 'sonner';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { withSuspense } from 'src/hoc';
import { InputSearch } from '@/components/ui/input-search';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { Button } from '@/components/ui/button';
import { EscrowMembershipRoleEnum } from 'src/graphql/schema';
import { EscrowMembershipCreateDocument } from 'src/graphql/actions/escrowMemberships/escrowMembershipCreate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { EmptyBlock } from '@/components/ui/empty-block';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';
import { cn } from 'src/lib/utils';

import { refetchEscrowMemberships } from '../api';
import { useMembershipsManagement } from '../model';

import type { IUsersQuery } from 'src/graphql/actions/user/users.query';
import type { IEscrowMembershipsQuery } from 'src/graphql/actions/escrowMemberships/escrowMemberships.query';

import AddUserDialog from './AddUserDialog';

type IUserRow = {
  id: string;
  user: {
    name: string;
    email: string;
    isAdded: boolean;
  };
};

const AddMemberButton = ({ id: userId = '', canAdd = false }) => {
  const { beneficiaryId } = useMembershipsManagement();
  const [createEscrowMembership, { loading }] = useMutation(EscrowMembershipCreateDocument);

  const onCreate = async () => {
    try {
      const { data } = await createEscrowMembership({
        variables: {
          input: {
            beneficiaryId,
            userId,
            escrowMembershipParams: {
              role: EscrowMembershipRoleEnum.Viewer,
            },
          },
        },
      });

      if (data?.escrowMembershipCreate?.success) {
        toast.success('Member access has been granted');
        void refetchEscrowMemberships();
      } else {
        throw new ClientError(data?.escrowMembershipCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  if (!canAdd) return <span className="font-semibold text-xs text-muted-foreground">Added</span>;

  return (
    <Button variant="ghost" size="sm" onClick={onCreate} disabled={loading} isLoading={loading}>
      Add
      <Plus />
    </Button>
  );
};

const columnHelper = createColumnHelper<IUserRow>();
const userColumn = columnHelper.accessor('user', {
  cell: (props) => {
    const { name, email } = props.getValue();
    const fallback = name?.charAt(0).toUpperCase();

    return (
      <div className="flex items-center gap-2">
        <Avatar className="size-6.5 rounded-md shrink-0">
          <AvatarImage src={getGravatarUrl(email)} />
          <AvatarFallback className="rounded-md bg-primary text-white">{fallback}</AvatarFallback>
        </Avatar>
        <div className="grow min-w-0 text-sm text-foreground truncate">{name}</div>
      </div>
    );
  },
});
const addMemberColumn = columnHelper.accessor('id', {
  cell: (props) => <AddMemberButton id={props.row.original.id} canAdd={!props.row.original.user.isAdded} />,
  meta: {
    cellClassName: 'text-right',
  },
});

const OrganizationMembers = () => {
  const [queryFilter, setQueryFilter] = useState<string | undefined>(undefined);
  const currentPage = useRef(1);
  const { queryEscrowMembershipsRef, canCreateMember } = useMembershipsManagement();

  const {
    data: { users },
    refetch,
    fetchMore,
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      page: 1,
      perPage: 20,
      ...(queryFilter && { queryFilter }),
    },
  });
  const [isPending, startSearchTransition] = useTransition();
  const tableRef = useRef<HTMLDivElement>(null);

  const {
    data: { escrowMemberships },
  } = useReadQuery(queryEscrowMembershipsRef);

  const handleLoadMore = () => {
    const nextPage = currentPage.current + 1;

    return new Promise<void>((resolve) => {
      startTransition(async () => {
        try {
          await fetchMore({
            variables: {
              page: nextPage,
              perPage: 20,
              ...(queryFilter && { queryFilter }),
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult?.users?.nodes?.length) return prev;

              currentPage.current = nextPage;

              return {
                ...prev,
                users: {
                  ...fetchMoreResult.users,
                  hasNextPage: fetchMoreResult.users.hasNextPage,
                  nodes: [...prev.users.nodes, ...fetchMoreResult.users.nodes],
                },
              };
            },
          });
        } catch (error) {
          toast.error('Failed to load more users');
        } finally {
          resolve();
        }
      });
    });
  };

  const { sentinelRef } = useInfiniteScroll({
    rootRef: tableRef,
    hasNextPage: users.hasNextPage && !isPending,
    onLoadMore: handleLoadMore,
  });

  const applySearch = (value: string | undefined) => {
    currentPage.current = 1;
    startSearchTransition(() => setQueryFilter(value));
  };

  const debouncedOnSearch = useRef(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => applySearch(e.target.value), 500),
  ).current;

  const handleClear = () => {
    debouncedOnSearch.cancel();
    applySearch(undefined);
  };

  const onClear = useCallback(handleClear, [refetch]);

  const filteredUsers = useMemo(
    () => filterUsers(users.nodes, escrowMemberships.nodes),
    [users.nodes, escrowMemberships.nodes],
  );

  const columns = useMemo(() => (canCreateMember ? [userColumn, addMemberColumn] : [userColumn]), [canCreateMember]);

  const table = useReactTable({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2.5 overflow-hidden flex flex-col py-3.5 px-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-extrabold">Add members</h2>
        <span className="text-xs text-muted-foreground shrink-0">Optional</span>
      </div>
      <InputSearch
        placeholder="Search name or email"
        onChange={debouncedOnSearch}
        onClear={onClear}
        isLoading={isPending}
      />
      <div className="rounded-md border border-border overflow-hidden">
        <Table ref={tableRef} maxHeight="max-h-[280px]" className="table-fixed">
          <colgroup>
            <col />
            {canCreateMember && <col className="w-25" />}
          </colgroup>
          <TableBody>
            {users.nodesCount ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className={cn('hover:bg-white', { 'border-0': table.getRowModel().rows.length - 1 === index })}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={cn('py-2.25', cell.column.columnDef.meta?.cellClassName)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-6">
                  <EmptyBlock
                    withCard={false}
                    icon={UserRoundSearch}
                    title={queryFilter ? 'No results found' : 'You don’t have members in your organization'}
                    action={<AddUserDialog onSuccess={handleClear} />}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableRow className="border-0 hover:bg-white">
              <TableCell colSpan={columns.length} className="p-0">
                <div ref={sentinelRef} aria-hidden="true" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(OrganizationMembers);

function filterUsers(
  users: IUsersQuery['users']['nodes'],
  memberships: IEscrowMembershipsQuery['escrowMemberships']['nodes'],
) {
  const usersSet = new Set(
    memberships.map(
      ({
        appSubscription: {
          user: { id },
        },
      }) => id,
    ),
  );

  return users.map((user) => ({
    id: user.id,
    user: {
      name: user.name || '',
      email: user.email,
      isAdded: usersSet.has(user.id),
    },
  }));
}
